import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { ssoConnect, ssoDisconnect } from '@client/new/api/sso.api';
import { GetInfluencerQuery } from 'generated/graphql';
import { getIdToken } from './oauth.utils';
import { SocialProvider } from './account.types';

const toApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (
      data &&
      typeof data === 'object' &&
      'message' in data &&
      typeof data.message === 'string'
    ) {
      return data.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

type Props = {
  socialAccounts: NonNullable<GetInfluencerQuery['creator']>['socialAccounts'];
  onRefresh: () => Promise<unknown> | void;
};

export const useConnectedAccounts = ({ socialAccounts, onRefresh }: Props) => {
  const pendingProviderRef = React.useRef<SocialProvider | null>(null);
  const [pendingProvider, setPendingProvider] =
    React.useState<SocialProvider | null>(null);
  const onRefreshRef = React.useRef(onRefresh);

  React.useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const setPending = (provider: SocialProvider | null) => {
    pendingProviderRef.current = provider;
    setPendingProvider(provider);
  };

  const connectedByProvider = React.useMemo(() => {
    const initialState: Record<
      SocialProvider,
      { connected: boolean; email: string | null }
    > = {
      google: { connected: false, email: null },
      apple: { connected: false, email: null },
    };

    for (const account of socialAccounts ?? []) {
      const provider = account.provider?.toLowerCase();

      if (provider === 'google') {
        initialState.google = {
          connected: account.connected,
          email: account.email,
        };
      }

      if (provider === 'apple') {
        initialState.apple = {
          connected: account.connected,
          email: account.email,
        };
      }
    }

    return initialState;
  }, [socialAccounts]);

  const handleDisconnect = React.useCallback(
    async (provider: SocialProvider) => {
      if (pendingProviderRef.current) {
        return;
      }

      setPending(provider);

      try {
        await ssoDisconnect({ provider });
        await onRefreshRef.current();
        toast.success('Account disconnected');
      } catch (error) {
        toast.error(toApiErrorMessage(error, 'Unable to disconnect account'));
      } finally {
        setPending(null);
      }
    },
    [],
  );

  const handleConnect = React.useCallback(async (provider: SocialProvider) => {
    if (pendingProviderRef.current) {
      return;
    }

    setPending(provider);

    try {
      const idToken = await getIdToken(provider);
      await ssoConnect({ provider, idToken });
      await onRefreshRef.current();
      toast.success('Account connected');
    } catch (error) {
      toast.error(toApiErrorMessage(error, 'Unable to connect account'));
    } finally {
      setPending(null);
    }
  }, []);

  return {
    connectedByProvider,
    pendingProvider,
    handleConnect,
    handleDisconnect,
  };
};
